import { readIdToken } from '../libs/auth'

type RequestType = 'get' | 'post' | 'put' | 'delete'
type ConfigType = {
  method: RequestType
  headers: Headers
  mode: string
  cache: string
  body?: string
}
type APIErrorType = {
  errno: string
  message?: string
}
// type ResponseType<DataType> =

class APIError<Type> extends Error {
  data: Type | undefined
  status: number | undefined

  constructor(msg: string) {
    super(msg)

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, APIError.prototype)
  }
}

export default class Fetcher {
  baseURL: string
  withToken: boolean

  static defaultConfigs = {
    mode: 'cors',
    cache: 'default',
  }

  static defaultHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  constructor(baseURL: string, withToken: boolean) {
    this.baseURL = baseURL
    this.withToken = withToken
  }

  getURL(url: string): string {
    return `${this.baseURL}${url}`
  }

  getHeaders(): Headers {
    const headers = new Headers(Fetcher.defaultHeaders)
    if (this.withToken) {
      const token = readIdToken()

      if (token.idToken && !token.isExpired) {
        headers.append('Authorization', token.idToken)
      }
    }
    return headers
  }

  async handleResponse<DataType>(res: Response): Promise<{
    data: DataType
    status: number
  }> {
    const { status, ok } = res
    let data: DataType | APIErrorType | never
    try {
      const resText = await res.text()
      data =
        resText === '' ? ({} as DataType) : (JSON.parse(resText) as DataType)
    } catch (ignored) {
      data = {} as DataType
    }
    if (!ok || status >= 400) {
      const err = new APIError(
        JSON.stringify((data as unknown as APIErrorType).message),
      )
      err.data = data
      err.status = status
      throw err
    }

    return {
      data,
      status,
    }
  }

  getConfigs<Type>(
    method: RequestType,
    data: Type,
    additionalHeaders?: Headers,
  ): RequestInit {
    const configs = {
      ...Fetcher.defaultConfigs,
      method,
      headers: this.getHeaders(),
    } as ConfigType
    if (additionalHeaders) {
      for (const [key, value] of Object.entries(additionalHeaders)) {
        configs.headers.append(key, JSON.stringify(value))
      }
    }
    if (method === 'post' || method === 'put') {
      configs.body = JSON.stringify(data)
    }
    return configs as RequestInit
  }

  async get<ResDataType>(
    url: string,
    headers?: Headers,
  ): Promise<{
    data: ResDataType
    status: number
  }> {
    const res = await fetch(
      this.getURL(url),
      this.getConfigs<null>('get', null, headers),
    )
    return this.handleResponse(res)
  }

  async post<DataType, ResDataType>(
    url: string,
    data?: DataType,
    headers?: Headers,
  ): Promise<{
    data: ResDataType
    status: number
  }> {
    const res = await fetch(
      this.getURL(url),
      this.getConfigs('post', data, headers),
    )
    return this.handleResponse(res)
  }

  async put<DataType, ResDataType>(
    url: string,
    data?: DataType,
    headers?: Headers,
  ): Promise<{
    data: ResDataType
    status: number
  }> {
    const res = await fetch(
      this.getURL(url),
      this.getConfigs('put', data, headers),
    )
    return this.handleResponse(res)
  }

  async delete<ResDataType>(
    url: string,
    headers?: Headers,
  ): Promise<{
    data: ResDataType
    status: number
  }> {
    const res = await fetch(
      this.getURL(url),
      this.getConfigs('delete', null, headers),
    )
    return this.handleResponse(res)
  }
}
